import PropTypes from "prop-types";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
// @mui
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
// locales
import { useTranslation } from "react-i18next";
// components
import Iconify from "src/components/iconify";
//
import {
  COMMAND_COLUMN_WIDTH,
  DEFAULT_LOCALE,
  DEFAULT_OPERATION,
  DEFAULT_PAGE_SIZE,
  GROUP_INDENT_WIDTH,
  INHERIT_HEIGHT_MAX_ROWS,
  ROW_CONTROL_SELECTOR,
  ROW_HEIGHT,
  STATE_SAVING_DELAY,
} from "./constants";
import { parseGridChildren } from "./config";
import {
  buildViewColumns,
  columnsMap,
  computeLayout,
  parseWidth,
  uniqueColumns,
} from "./core/layout";
import { createEmitter } from "./core/emitter";
import { readStorage, writeStorage } from "./core/storage";
import { GridRoot } from "./styles";
import { useGridData } from "./core/data-controller";
import {
  applySavedState,
  createInitialState,
  gridStateReducer,
  initialColumnState,
  toSavedState,
} from "./core/state";
import {
  buildDataSourceFilter,
  cloneFilter,
  getColumnSelector,
  getFilterRowCondition,
  getHeaderFilterCondition,
  joinWith,
  setFilterRowCondition,
  setHeaderFilterCondition,
} from "./core/filter";
import { calculateCellValue, getFieldValue, getValueText, isDateType } from "./core/format";
import { exportGrid } from "./core/export";
import { resolveSource } from "./store/data-source";
import { compileFilter, queryLocal, sortItems } from "./store/local-query";
import { DataRow, GroupRow } from "./components/grid-rows";
import HeaderRow from "./components/header-row";
import FilterRow from "./components/filter-row";
import GridToolbar from "./components/grid-toolbar";
import ColumnMenu from "./components/column-menu";
import ColumnChooser from "./components/column-chooser";
import HeaderFilterPopover from "./components/header-filter";
import FilterPanel from "./components/filter-panel";
import FilterBuilderDialog from "./components/filter-builder";
import Pager from "./components/pager";

const DEFAULT_TOOLBAR_ITEMS = [
  { name: "groupPanel", location: "before" },
  { name: "exportButton", location: "after" },
  { name: "columnChooserButton", location: "after" },
];

const DataGrid = forwardRef(function DataGrid(props, ref) {
  const {
    children,
    dataSource,
    keyExpr,
    height,
    width,
    id,
    className,
    style,
    showBorders = false,
    showColumnLines = false,
    showRowLines = true,
    rowAlternationEnabled = false,
    hoverStateEnabled = true,
    allowColumnResizing = false,
    allowColumnReordering = false,
    noDataText,
    filterValue: filterValueProp,
    defaultFilterValue,
    toolbarItems,
    groupToggle = true,
    locale: localeProp,
    onRowClick,
    onRowPrepared,
  } = props;

  const { t, i18n } = useTranslation();
  const locale = useMemo(
    () => ({ ...DEFAULT_LOCALE, blankText: t("gridBlank"), ...localeProp }),
    [localeProp, t]
  );

  const parsed = useMemo(() => parseGridChildren(children), [children]);
  const columns = useMemo(() => uniqueColumns(parsed.columns), [parsed.columns]);
  const childOptions = parsed.options;

  const paging = childOptions.paging || {};
  const pagerOptions = childOptions.pager || {};
  const scrollingMode = childOptions.scrolling?.mode;
  const pagingEnabled = paging.enabled !== false;
  const mode =
    !pagingEnabled || scrollingMode === "infinite" || scrollingMode === "virtual"
      ? "infinite"
      : "standard";
  const initialPageSize = pagingEnabled
    ? paging.pageSize ?? paging.defaultPageSize ?? DEFAULT_PAGE_SIZE
    : 100000;
  const allowedPageSizesKey = JSON.stringify(pagerOptions.allowedPageSizes ?? null);
  const allowedPageSizes = useMemo(() => {
    const configured = JSON.parse(allowedPageSizesKey);

    return Array.isArray(configured)
      ? configured
      : [Math.max(Math.round(initialPageSize / 2), 1), initialPageSize, initialPageSize * 2];
  }, [allowedPageSizesKey, initialPageSize]);
  const filterRowVisible = !!childOptions.filterRow?.visible;
  const headerFilterVisible = !!childOptions.headerFilter?.visible;
  const groupPanelVisible = !!childOptions.groupPanel?.visible;
  const columnChooserEnabled = !!childOptions.columnChooser?.enabled;
  const filterPanelVisible = !!childOptions.filterPanel?.visible;
  const columnFixingEnabled = !!childOptions.columnFixing?.enabled;
  const sortingMode = childOptions.sorting?.mode ?? "single";
  const selectionOptions = childOptions.selection || {};
  const selectionMode = selectionOptions.mode || "none";
  const rowDragging = childOptions.rowDragging || {};
  const rowDraggingEnabled = !!rowDragging.allowReordering;
  const autoExpandAll = childOptions.grouping?.autoExpandAll !== false;
  const exportOptions = childOptions.export || {};
  const exportEnabled = !!exportOptions.enabled;
  const stateStoring = childOptions.stateStoring || {};
  const storageKey =
    stateStoring.enabled && stateStoring.storageKey ? stateStoring.storageKey : null;
  const { remoteOperations } = childOptions;
  const remote =
    !!remoteOperations &&
    (remoteOperations === true ||
      ["paging", "filtering", "sorting", "grouping"].some(name => remoteOperations[name]));
  const loadPanelEnabled = childOptions.loadPanel?.enabled !== false;

  const [state, dispatch] = useReducer(gridStateReducer, null, () => {
    const initial = createInitialState({
      columns,
      filterValue: filterValueProp,
      defaultFilterValue,
      pageSize: initialPageSize,
    });

    return storageKey
      ? applySavedState(initial, readStorage(storageKey), columns, allowedPageSizes)
      : initial;
  });

  const [refreshToken, setRefreshToken] = useState(0);
  const [renderVersion, setRenderVersion] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [natural, setNatural] = useState({ key: null, widths: {} });
  const [selection, setSelection] = useState(() => ({ keys: [], data: new Map() }));
  const [menuTarget, setMenuTarget] = useState(null);
  const [headerFilterTarget, setHeaderFilterTarget] = useState(null);
  const [chooserAnchor, setChooserAnchor] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [dragState, setDragState] = useState(null);

  const propsRef = useRef(props);
  const stateRef = useRef(state);
  const columnsRef = useRef(columns);
  const handlersRef = useRef({ onRowPrepared });
  const selectionRef = useRef(selection);
  const selectionModeRef = useRef(selectionMode);
  const rowDraggingRef = useRef(rowDragging);
  const localeRef = useRef(locale);
  const tRef = useRef(t);
  const rootRef = useRef(null);
  const scrollRef = useRef(null);
  const tableRef = useRef(null);
  const bodyRef = useRef(null);
  const emitterRef = useRef(null);
  const instanceRef = useRef(null);
  const draggedColumnRef = useRef(null);
  const selectedOperationsRef = useRef(new Map());
  const rowVersionsRef = useRef(new Map());
  const refreshWaitersRef = useRef([]);
  const lastClickedIndexRef = useRef(null);
  const previousColumnPropsRef = useRef(null);
  const storageKeyRef = useRef(storageKey);
  const allowedPageSizesRef = useRef(allowedPageSizes);

  if (!emitterRef.current) emitterRef.current = createEmitter();
  if (!instanceRef.current) instanceRef.current = {};

  propsRef.current = props;
  stateRef.current = state;
  columnsRef.current = columns;
  handlersRef.current = { onRowPrepared };
  selectionRef.current = selection;
  selectionModeRef.current = selectionMode;
  rowDraggingRef.current = rowDragging;
  localeRef.current = locale;
  tRef.current = t;
  storageKeyRef.current = storageKey;
  allowedPageSizesRef.current = allowedPageSizes;

  const getColumnState = useCallback(
    column => stateRef.current.columns[column.id] ?? initialColumnState(column, column.index),
    []
  );

  const columnsKey = columns.map(column => column.id).join("|");

  useLayoutEffect(() => {
    dispatch({ type: "syncColumns", columns: columnsRef.current });
  }, [columnsKey]);

  useLayoutEffect(() => {
    const previous = previousColumnPropsRef.current;
    const next = new Map();

    columns.forEach(column => {
      const snapshot = {
        visible: column.visible,
        sortOrder: column.sortOrder,
        groupIndex: column.groupIndex,
        fixed: column.fixed,
        fixedPosition: column.fixedPosition,
      };
      const before = previous?.get(column.id);

      next.set(column.id, snapshot);

      if (!before) return;

      const patch = {};

      if (before.visible !== snapshot.visible) patch.visible = snapshot.visible !== false;
      if (before.sortOrder !== snapshot.sortOrder) patch.sortOrder = snapshot.sortOrder;
      if (before.groupIndex !== snapshot.groupIndex) patch.groupIndex = snapshot.groupIndex;
      if (before.fixed !== snapshot.fixed) patch.fixed = !!snapshot.fixed;
      if (before.fixedPosition !== snapshot.fixedPosition && snapshot.fixedPosition)
        patch.fixedPosition = snapshot.fixedPosition;

      if (Object.keys(patch).length) dispatch({ type: "columnOption", id: column.id, patch });
    });

    previousColumnPropsRef.current = next;
  }, [columns]);

  const source = useMemo(() => resolveSource(dataSource, keyExpr), [dataSource, keyExpr]);
  const sourceRef = useRef(source);

  sourceRef.current = source;

  const columnsBySelector = useMemo(() => columnsMap(columns), [columns]);
  const columnsBySelectorRef = useRef(columnsBySelector);

  columnsBySelectorRef.current = columnsBySelector;

  const getGetter = useCallback(selector => {
    if (typeof selector === "function") return selector;

    const column = columnsBySelectorRef.current[selector];

    if (column?.calculateCellValue) return item => calculateCellValue(column, item);

    return item => getFieldValue(item, selector);
  }, []);

  const orderedColumns = useMemo(
    () =>
      columns
        .slice()
        .sort(
          (a, b) =>
            (state.columns[a.id]?.order ?? a.index) - (state.columns[b.id]?.order ?? b.index) ||
            a.index - b.index
        ),
    [columns, state.columns]
  );

  const groupedColumns = useMemo(
    () =>
      orderedColumns
        .filter(
          column => column.hasData && typeof state.columns[column.id]?.groupIndex === "number"
        )
        .sort((a, b) => state.columns[a.id].groupIndex - state.columns[b.id].groupIndex)
        .map(column => ({ column, state: state.columns[column.id] })),
    [orderedColumns, state.columns]
  );

  const groups = useMemo(
    () =>
      groupedColumns.map(({ column, state: columnState }) => ({
        selector: getColumnSelector(column),
        desc: columnState.sortOrder === "desc",
        column,
      })),
    [groupedColumns]
  );

  const sort = useMemo(
    () =>
      orderedColumns
        .filter(column => {
          const columnState = state.columns[column.id];

          return (
            columnState?.sortOrder &&
            typeof columnState.groupIndex !== "number" &&
            column.hasData &&
            (!remote || column.dataField)
          );
        })
        .sort((a, b) => (state.columns[a.id].sortIndex ?? 0) - (state.columns[b.id].sortIndex ?? 0))
        .map(column => ({
          selector: remote ? column.dataField : getColumnSelector(column),
          desc: state.columns[column.id].sortOrder === "desc",
        })),
    [orderedColumns, remote, state.columns]
  );

  const filter = useMemo(
    () =>
      state.filterEnabled
        ? buildDataSourceFilter(state.filterValue, columnsBySelector, "filterRow")
        : null,
    [columnsBySelector, state.filterEnabled, state.filterValue]
  );

  const data = useGridData({
    source,
    remote,
    filter,
    sort,
    groups,
    mode,
    pageSize: state.pageSize,
    pageIndex: state.pageIndex,
    autoExpandAll,
    getGetter,
    refreshToken,
  });

  const dataRef = useRef(data);

  dataRef.current = data;

  const scrollResetKey = JSON.stringify([
    filter,
    sort,
    groups.map(({ selector, desc }) => [selector, desc]),
    state.pageIndex,
    state.pageSize,
  ]);

  useLayoutEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [scrollResetKey]);

  useLayoutEffect(() => {
    const headerRow = tableRef.current?.querySelector("thead tr.cg-header-row");
    const root = rootRef.current;

    if (!headerRow || !root) return;

    const value = `${headerRow.getBoundingClientRect().height}px`;

    if (root.style.getPropertyValue("--cg-header-height") !== value)
      root.style.setProperty("--cg-header-height", value);
  });

  const groupsRef = useRef(groups);
  const sortRef = useRef(sort);
  const remoteRef = useRef(remote);

  groupsRef.current = groups;
  sortRef.current = sort;
  remoteRef.current = remote;

  useEffect(() => {
    if (!storageKey) return undefined;

    const timer = setTimeout(() => {
      writeStorage(
        storageKey,
        toSavedState(state, columnsRef.current, {
          allowedPageSizes: allowedPageSizesRef.current,
          selectedRowKeys: selectionRef.current.keys,
        })
      );
    }, STATE_SAVING_DELAY);

    return () => clearTimeout(timer);
  }, [state, storageKey]);

  const visibleDataColumns = useMemo(
    () =>
      orderedColumns.filter(column => {
        const columnState = state.columns[column.id];

        return (
          !column.isSelection &&
          (columnState ? columnState.visible : column.visible !== false) &&
          typeof columnState?.groupIndex !== "number"
        );
      }),
    [orderedColumns, state.columns]
  );

  const selectionColumn = useMemo(() => columns.find(column => column.isSelection), [columns]);

  const commandColumns = useMemo(() => {
    const list = [];

    if (selectionMode === "multiple")
      list.push({
        kind: "select",
        key: "__select",
        width: parseWidth(selectionColumn?.width) ?? COMMAND_COLUMN_WIDTH,
        column: selectionColumn,
      });

    if (rowDraggingEnabled && rowDragging.showDragIcons !== false)
      list.push({ kind: "drag", key: "__drag", width: 36 });

    groupedColumns.forEach(({ column }, level) =>
      list.push({
        kind: "indent",
        key: `__group${level}`,
        level,
        width: GROUP_INDENT_WIDTH,
        column,
      })
    );

    return list;
  }, [
    groupedColumns,
    rowDragging.showDragIcons,
    rowDraggingEnabled,
    selectionColumn,
    selectionMode,
  ]);

  const commandWidth = commandColumns.reduce((sum, column) => sum + column.width, 0);
  const measureKey = `${visibleDataColumns.map(column => column.id).join("|")}#${
    data.rows.length > 0 ? 1 : 0
  }`;
  const measuring = natural.key !== measureKey && containerWidth > 0;

  const layout = useMemo(
    () =>
      computeLayout({
        dataColumns: visibleDataColumns,
        commandWidth,
        getState: column => state.columns[column.id] ?? initialColumnState(column, column.index),
        natural: natural.widths,
        containerWidth,
      }),
    [commandWidth, containerWidth, natural.widths, state.columns, visibleDataColumns]
  );

  const viewColumns = useMemo(
    () => buildViewColumns(commandColumns, visibleDataColumns, state.columns, layout),
    [commandColumns, layout, state.columns, visibleDataColumns]
  );

  const viewColumnsRef = useRef(viewColumns);
  const visibleColumnsRef = useRef(visibleDataColumns);
  const layoutRef = useRef(layout);

  viewColumnsRef.current = viewColumns;
  visibleColumnsRef.current = visibleDataColumns;
  layoutRef.current = layout;

  useLayoutEffect(() => {
    if (!measuring || !tableRef.current) return;

    const widths = {};

    tableRef.current.querySelectorAll("thead tr.cg-header-row > th").forEach(cell => {
      const viewColumn = viewColumns[Number(cell.dataset.colIndex)];

      if (viewColumn?.kind === "data")
        widths[viewColumn.key] = Math.ceil(cell.getBoundingClientRect().width) + 2;
    });

    setNatural({ key: measureKey, widths });
  }, [measureKey, measuring, viewColumns]);

  useLayoutEffect(() => {
    const element = scrollRef.current;

    if (!element) return undefined;

    setContainerWidth(element.clientWidth);

    if (typeof ResizeObserver === "undefined") return undefined;

    let frame = null;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setContainerWidth(element.clientWidth));
    });

    observer.observe(element);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  const selectedSet = useMemo(() => new Set(selection.keys), [selection.keys]);

  const commitSelection = useCallback((nextKeys, dataMap) => {
    const { current } = selectionRef;
    const previous = new Set(current.keys);
    const next = new Set(nextKeys);
    const added = nextKeys.filter(key => !previous.has(key));
    const removed = current.keys.filter(key => !next.has(key));

    if (!added.length && !removed.length) return;

    const nextData = new Map(dataMap || current.data);

    removed.forEach(key => nextData.delete(key));

    const nextSelection = { keys: nextKeys, data: nextData };

    selectionRef.current = nextSelection;
    setSelection(nextSelection);

    const event = {
      component: instanceRef.current,
      selectedRowKeys: nextKeys.slice(),
      selectedRowsData: nextKeys.map(key => nextData.get(key)).filter(item => item !== undefined),
      currentSelectedRowKeys: added,
      currentDeselectedRowKeys: removed,
    };

    emitterRef.current.emit("selectionChanged", event);
    propsRef.current.onSelectionChanged?.(event);
  }, []);

  const loadKeysData = useCallback(async keys => {
    const { current } = sourceRef;
    const keyExpression = current.key;

    if (!keys.length || !keyExpression) return;

    let items;

    if (current.kind === "local") {
      const set = new Set(keys);

      items = (current.getItems() || []).filter(item =>
        set.has(getFieldValue(item, keyExpression))
      );
    } else {
      const result = await current.load({
        filter: joinWith(
          keys.map(key => [keyExpression, "=", key]),
          "or"
        ),
      });

      items = result.data;
    }

    if (!items.length) return;

    const selectionNow = selectionRef.current;
    const nextData = new Map(selectionNow.data);

    items.forEach(item => {
      const key = getFieldValue(item, keyExpression);

      if (selectionNow.keys.includes(key)) nextData.set(key, item);
    });

    const nextSelection = { keys: selectionNow.keys, data: nextData };

    selectionRef.current = nextSelection;
    setSelection(nextSelection);
  }, []);

  const selectRows = useCallback(
    (keys, preserve) => {
      const list = (Array.isArray(keys) ? keys : [keys]).filter(key => key !== undefined);
      const { current } = selectionRef;
      let nextKeys;

      if (selectionModeRef.current === "single")
        nextKeys = list.length ? [list[list.length - 1]] : [];
      else if (preserve)
        nextKeys = [...current.keys, ...list.filter(key => !current.keys.includes(key))];
      else nextKeys = list.slice();

      const dataMap = new Map(current.data);
      const missing = [];

      nextKeys.forEach(key => {
        if (dataMap.has(key)) return;

        const row = dataRef.current.rows.find(item => item.rowType === "data" && item.key === key);

        if (row) dataMap.set(key, row.data);
        else missing.push(key);
      });

      commitSelection(nextKeys, dataMap);

      if (missing.length)
        return loadKeysData(missing)
          .catch(() => undefined)
          .then(() => selectionRef.current.keys);

      return Promise.resolve(nextKeys);
    },
    [commitSelection, loadKeysData]
  );

  const deselectRows = useCallback(
    keys => {
      const list = new Set(Array.isArray(keys) ? keys : [keys]);
      const nextKeys = selectionRef.current.keys.filter(key => !list.has(key));

      commitSelection(nextKeys);

      return Promise.resolve(nextKeys);
    },
    [commitSelection]
  );

  const clearSelection = useCallback(() => commitSelection([]), [commitSelection]);

  const toggleRowSelection = useCallback(
    (row, checked) => {
      if (checked) selectRows([row.key], selectionModeRef.current === "multiple");
      else deselectRows([row.key]);
    },
    [deselectRows, selectRows]
  );

  const repaint = useCallback(() => setRenderVersion(version => version + 1), []);

  const repaintRows = useCallback(indexes => {
    const { rows } = dataRef.current;

    (Array.isArray(indexes) ? indexes : [indexes]).forEach(index => {
      const row = rows[index];

      if (!row) return;

      const key = row.rowType === "group" ? JSON.stringify(row.key) : row.key;

      rowVersionsRef.current.set(key, (rowVersionsRef.current.get(key) || 0) + 1);
    });

    setRenderVersion(version => version + 1);
  }, []);

  const refresh = useCallback(
    () =>
      new Promise(resolve => {
        refreshWaitersRef.current.push({ resolve, version: dataRef.current.version });
        setRefreshToken(token => token + 1);
      }),
    []
  );

  useEffect(() => {
    if (data.phase !== "idle" || !refreshWaitersRef.current.length) return;

    const ready = refreshWaitersRef.current.filter(
      waiter => waiter.version < data.version || data.error
    );

    refreshWaitersRef.current = refreshWaitersRef.current.filter(waiter => !ready.includes(waiter));
    ready.forEach(waiter => waiter.resolve());
  }, [data.error, data.phase, data.version]);

  const setFilterValue = useCallback(
    filterValue => dispatch({ type: "filterValue", filterValue: cloneFilter(filterValue ?? null) }),
    []
  );

  const clearFilter = useCallback(
    () => setFilterValue(propsRef.current.defaultFilterValue ?? null),
    [setFilterValue]
  );

  const combinedFilter = useCallback(() => {
    const { current } = stateRef;

    if (!current.filterEnabled) return null;

    return buildDataSourceFilter(current.filterValue, columnsMap(columnsRef.current), "filterRow");
  }, []);

  const loadAllData = useCallback(
    async (extraFilter, includeSort = true) => {
      const { current } = sourceRef;
      const fullSort = includeSort
        ? [
            ...groupsRef.current.map(({ selector, desc }) => ({ selector, desc })),
            ...sortRef.current,
          ]
        : [];
      const fullFilter = joinWith([combinedFilter(), extraFilter].filter(Boolean), "and");

      if (current.kind === "local" || !remoteRef.current) {
        const items =
          current.kind === "local" ? current.getItems() || [] : (await current.load({})).data;
        const filtered = fullFilter ? items.filter(compileFilter(fullFilter, getGetter)) : items;

        return sortItems(filtered, fullSort, getGetter);
      }

      const result = await current.load({
        filter: fullFilter,
        sort: fullSort.length ? fullSort : undefined,
      });

      return result.data;
    },
    [combinedFilter, getGetter]
  );

  const exportTo = useCallback(
    async format => {
      const event = {
        component: instanceRef.current,
        format,
        cancel: false,
        fileName: propsRef.current.exportFileName,
      };

      propsRef.current.onExporting?.(event);

      if (event.cancel) return;

      await exportGrid({
        format,
        fileName: event.fileName,
        title: event.fileName,
        columns: visibleColumnsRef.current.filter(column => column.allowExporting),
        groups: groupsRef.current,
        loadAll: () => loadAllData(null, true),
        widths: layoutRef.current.widths,
        locale: localeRef.current,
        t: tRef.current,
      });
    },
    [loadAllData]
  );

  const setColumnOption = useCallback((column, patch) => {
    dispatch({ type: "columnOption", id: column.id, patch });
  }, []);

  const findColumn = useCallback(identifier => {
    const list = columnsRef.current;

    if (typeof identifier === "number") return list[identifier];

    return list.find(
      column =>
        column.id === identifier ||
        column.name === identifier ||
        column.dataField === identifier ||
        column.caption === identifier
    );
  }, []);

  const toggleGroup = useCallback(row => {
    dataRef.current.setGroupExpanded(row.key, !row.isExpanded);
  }, []);

  const createInitialStateNow = useCallback(
    () =>
      createInitialState({
        columns: columnsRef.current,
        filterValue: undefined,
        defaultFilterValue: propsRef.current.defaultFilterValue,
        pageSize: initialPageSize,
      }),
    [initialPageSize]
  );

  const storeFacade = useMemo(
    () => ({
      key: () => source.key,
      load: async (loadOptions = {}) => {
        if (source.kind === "local")
          return queryLocal(source.getItems() || [], loadOptions, getGetter).data;

        const result = await source.load(loadOptions);

        return result.data;
      },
      byKey: async key => {
        const items = await loadAllData([source.key, "=", key], false);

        return items[0];
      },
      _array: source.kind === "local" ? source.getItems() : undefined,
    }),
    [getGetter, loadAllData, source]
  );

  Object.assign(instanceRef.current, {
    refresh,
    repaint,
    repaintRows,
    getVisibleRows: () =>
      dataRef.current.rows.map((row, rowIndex) => ({
        ...row,
        rowIndex,
        isSelected: row.rowType === "data" && selectionRef.current.keys.includes(row.key),
      })),
    getSelectedRowsData: () =>
      selectionRef.current.keys
        .map(key => selectionRef.current.data.get(key))
        .filter(item => item !== undefined),
    getSelectedRowKeys: () => selectionRef.current.keys.slice(),
    selectRows,
    deselectRows,
    selectRowsByIndexes: indexes =>
      selectRows(
        (Array.isArray(indexes) ? indexes : [indexes])
          .map(index => dataRef.current.rows[index])
          .filter(row => row?.rowType === "data")
          .map(row => row.key),
        false
      ),
    selectAll: () =>
      selectRows(
        dataRef.current.rows.filter(row => row.rowType === "data").map(row => row.key),
        true
      ),
    deselectAll: () => clearSelection(),
    clearSelection,
    isRowSelected: key => selectionRef.current.keys.includes(key),
    on: (event, handler) => emitterRef.current.on(event, handler),
    off: (event, handler) => emitterRef.current.off(event, handler),
    getDataSource: () => ({
      items: () => dataRef.current.getLoadedItems(),
      get _items() {
        return dataRef.current.getLoadedItems();
      },
      group: () => groupsRef.current.map(({ selector, desc }) => ({ selector, desc })),
      filter: () => combinedFilter(),
      sort: () => sortRef.current,
      store: () => storeFacade,
      reload: () => refresh(),
      load: () => refresh(),
      totalCount: () => dataRef.current.totalCount,
      isLoading: () => dataRef.current.phase !== "idle",
      pageIndex: () => stateRef.current.pageIndex,
      pageSize: () => stateRef.current.pageSize,
      key: () => sourceRef.current.key,
    }),
    getCombinedFilter: () => combinedFilter(),
    clearFilter: () => setFilterValue(null),
    filter: filterValue => {
      if (filterValue === undefined) return stateRef.current.filterValue;

      setFilterValue(filterValue);

      return undefined;
    },
    clearSorting: () => dispatch({ type: "clearSorting" }),
    clearGrouping: () => dispatch({ type: "ungroupAll" }),
    state: savedState => {
      if (savedState === undefined)
        return toSavedState(stateRef.current, columnsRef.current, {
          allowedPageSizes: allowedPageSizesRef.current,
          selectedRowKeys: selectionRef.current.keys,
        });

      const initial = createInitialStateNow();

      dispatch({
        type: "reset",
        state: savedState
          ? applySavedState(initial, savedState, columnsRef.current, allowedPageSizesRef.current)
          : initial,
      });

      return undefined;
    },
    option: (name, value) => {
      const { current } = stateRef;

      if (value === undefined) {
        switch (name) {
          case "filterValue":
            return current.filterValue;
          case "filterPanel.filterEnabled":
            return current.filterEnabled;
          case "paging.pageSize":
            return current.pageSize;
          case "paging.pageIndex":
            return current.pageIndex;
          case "selectedRowKeys":
            return selectionRef.current.keys.slice();
          case "columns":
            return columnsRef.current;
          case "stateStoring.storageKey":
            return storageKeyRef.current;
          default:
            return propsRef.current[name];
        }
      }

      switch (name) {
        case "filterValue":
          setFilterValue(value);
          break;
        case "filterPanel.filterEnabled":
          dispatch({ type: "filterEnabled", enabled: !!value });
          break;
        case "paging.pageSize":
          dispatch({ type: "pageSize", pageSize: value });
          break;
        case "paging.pageIndex":
          dispatch({ type: "pageIndex", pageIndex: value });
          break;
        case "selectedRowKeys":
          selectRows(value || [], false);
          break;
        default:
          break;
      }

      return undefined;
    },
    columnOption: (identifier, option, value) => {
      const column = findColumn(identifier);

      if (!column) return undefined;

      if (option && typeof option === "object") {
        setColumnOption(column, option);
        return undefined;
      }

      const columnState = getColumnState(column);

      if (value === undefined)
        return option ? columnState[option] ?? column[option] : { ...column, ...columnState };

      setColumnOption(column, { [option]: value });

      return undefined;
    },
    expandAll: () => dataRef.current.setAllExpanded(true),
    collapseAll: () => dataRef.current.setAllExpanded(false),
    expandRow: key => {
      dataRef.current.setGroupExpanded(Array.isArray(key) ? key : [key], true);

      return Promise.resolve();
    },
    collapseRow: key => {
      dataRef.current.setGroupExpanded(Array.isArray(key) ? key : [key], false);

      return Promise.resolve();
    },
    isRowExpanded: key => dataRef.current.isGroupExpanded(Array.isArray(key) ? key : [key]),
    pageIndex: index => {
      if (index === undefined) return stateRef.current.pageIndex;

      dispatch({ type: "pageIndex", pageIndex: index });

      return Promise.resolve();
    },
    pageSize: size => {
      if (size === undefined) return stateRef.current.pageSize;

      dispatch({ type: "pageSize", pageSize: size });

      return undefined;
    },
    pageCount: () => dataRef.current.pageCount,
    totalCount: () => dataRef.current.totalCount,
    getRowIndexByKey: key =>
      dataRef.current.rows.findIndex(row => row.rowType === "data" && row.key === key),
    getKeyByRowIndex: index => dataRef.current.rows[index]?.key,
    getRowElement: index =>
      tableRef.current?.querySelectorAll(`tbody tr[data-row-index="${index}"]`) || [],
    getCellElement: (rowIndex, identifier) => {
      const column = findColumn(identifier);
      const viewColumn = viewColumnsRef.current.find(item => item.column === column);

      if (!viewColumn) return undefined;

      return tableRef.current?.querySelector(
        `tbody tr[data-row-index="${rowIndex}"] > td[data-col-index="${viewColumn.index}"]`
      );
    },
    saveEditData: () => {
      repaint();

      return Promise.resolve();
    },
    cancelEditData: () => undefined,
    hasEditData: () => false,
    updateDimensions: () => setNatural(current => ({ ...current, key: null })),
    beginUpdate: () => undefined,
    endUpdate: () => undefined,
    focus: () => scrollRef.current?.focus(),
    element: () => rootRef.current,
    exportTo,
    getScrollable: () => ({
      scrollTo: position =>
        scrollRef.current?.scrollTo(typeof position === "number" ? { top: position } : position),
      scrollTop: () => scrollRef.current?.scrollTop ?? 0,
    }),
  });

  useImperativeHandle(ref, () => ({ instance: instanceRef.current }), []);

  const initializedRef = useRef(false);

  if (!initializedRef.current) {
    initializedRef.current = true;
    props.onInitialized?.({ component: instanceRef.current, element: null });
  }

  useEffect(() => {
    if (data.phase !== "idle" || data.error) return;

    const event = { component: instanceRef.current, element: rootRef.current };

    emitterRef.current.emit("contentReady", event);
    propsRef.current.onContentReady?.(event);
  }, [data.error, data.phase, data.version]);

  const handleScroll = useCallback(event => {
    const element = event.currentTarget;
    const { current } = dataRef;

    if (!current.hasMore || current.phase !== "idle" || current.error) return;

    if (element.scrollHeight - element.scrollTop - element.clientHeight < ROW_HEIGHT * 6)
      current.loadMore();
  }, []);

  useEffect(() => {
    const element = scrollRef.current;
    const { current } = dataRef;

    if (!element || !current.hasMore || current.phase !== "idle" || current.error) return;

    if (element.scrollHeight <= element.clientHeight + ROW_HEIGHT * 2) current.loadMore();
  }, [data.hasMore, data.phase, data.version, data.error, containerWidth]);

  const handleBodyClick = useCallback(
    event => {
      const rowElement = event.target.closest("tr[data-row-index]");

      if (!rowElement || !tableRef.current?.contains(rowElement)) return;

      const rowIndex = Number(rowElement.dataset.rowIndex);
      const row = dataRef.current.rows[rowIndex];

      if (!row) return;

      const component = instanceRef.current;
      const cellElement = event.target.closest("td[data-col-index]");
      const viewColumn = cellElement
        ? viewColumnsRef.current[Number(cellElement.dataset.colIndex)]
        : null;
      const currentMode = selectionModeRef.current;

      if (event.target.closest(".cg-command-select, .cg-command-drag")) return;

      const control = event.target.closest(ROW_CONTROL_SELECTOR);
      const fromControl = !!control && rowElement.contains(control);

      if (row.rowType === "data" && currentMode !== "none" && !fromControl) {
        const ctrl = event.ctrlKey || event.metaKey;
        const { keys } = selectionRef.current;

        if (currentMode === "single") {
          if (ctrl && keys.includes(row.key)) deselectRows([row.key]);
          else selectRows([row.key], false);
        } else if (event.shiftKey && lastClickedIndexRef.current != null) {
          const [from, to] = [lastClickedIndexRef.current, rowIndex].sort((a, b) => a - b);

          selectRows(
            dataRef.current.rows
              .slice(from, to + 1)
              .filter(item => item.rowType === "data")
              .map(item => item.key),
            true
          );
        } else if (ctrl) {
          if (keys.includes(row.key)) deselectRows([row.key]);
          else selectRows([row.key], true);
        } else selectRows([row.key], false);

        lastClickedIndexRef.current = rowIndex;
      }

      const rowEvent = {
        component,
        element: rootRef.current,
        rowElement,
        rowIndex,
        rowType: row.rowType,
        data: row.data,
        key: row.key,
        event,
        isSelected: row.rowType === "data" && selectionRef.current.keys.includes(row.key),
        isExpanded: row.isExpanded,
        groupIndex: row.groupIndex,
        values: row.values || [],
      };

      if (viewColumn && propsRef.current.onCellClick) {
        const value =
          viewColumn.kind === "data" && row.rowType === "data"
            ? calculateCellValue(viewColumn.column, row.data)
            : undefined;

        propsRef.current.onCellClick({
          ...rowEvent,
          cellElement,
          column: viewColumn.column,
          columnIndex: viewColumn.index,
          value,
          displayValue: value,
          text:
            viewColumn.kind === "data" && value != null
              ? getValueText(viewColumn.column, value, localeRef.current)
              : "",
          row: { data: row.data, key: row.key, rowIndex, rowType: row.rowType },
        });
      }

      propsRef.current.onRowClick?.(rowEvent);
    },
    [deselectRows, selectRows]
  );

  const handleBodyDoubleClick = useCallback(event => {
    const rowElement = event.target.closest("tr[data-row-index]");

    if (!rowElement || !propsRef.current.onRowDblClick) return;

    const rowIndex = Number(rowElement.dataset.rowIndex);
    const row = dataRef.current.rows[rowIndex];

    if (!row) return;

    propsRef.current.onRowDblClick({
      component: instanceRef.current,
      element: rootRef.current,
      rowElement,
      rowIndex,
      rowType: row.rowType,
      data: row.data,
      key: row.key,
      event,
    });
  }, []);

  const handleBodyMouseOver = useCallback(event => {
    const cell = event.target.closest("td");

    if (!cell || cell.childElementCount) return;

    if (cell.scrollWidth > cell.clientWidth) cell.title = cell.textContent;
    else cell.removeAttribute("title");
  }, []);

  useEffect(() => {
    const body = bodyRef.current;

    if (!body) return undefined;

    body.addEventListener("click", handleBodyClick);
    body.addEventListener("dblclick", handleBodyDoubleClick);
    body.addEventListener("mouseover", handleBodyMouseOver);

    return () => {
      body.removeEventListener("click", handleBodyClick);
      body.removeEventListener("dblclick", handleBodyDoubleClick);
      body.removeEventListener("mouseover", handleBodyMouseOver);
    };
  }, [handleBodyClick, handleBodyDoubleClick, handleBodyMouseOver]);

  const startRowDrag = useCallback((event, rowIndex) => {
    if (event.button !== 0) return;

    event.preventDefault();
    event.stopPropagation();

    const scrollElement = scrollRef.current;
    const { rows } = dataRef.current;
    let target = rowIndex;

    setDragState({ from: rowIndex, target: rowIndex });

    const handleMove = moveEvent => {
      const element = document.elementFromPoint(moveEvent.clientX, moveEvent.clientY);
      const rowElement = element?.closest?.("tbody tr[data-row-index]");

      if (scrollElement) {
        const rect = scrollElement.getBoundingClientRect();

        if (moveEvent.clientY < rect.top + ROW_HEIGHT * 2) scrollElement.scrollTop -= 12;
        else if (moveEvent.clientY > rect.bottom - ROW_HEIGHT) scrollElement.scrollTop += 12;
      }

      if (!rowElement || !scrollElement?.contains(rowElement)) return;

      const index = Number(rowElement.dataset.rowIndex);
      const rect = rowElement.getBoundingClientRect();
      const slot = moveEvent.clientY < rect.top + rect.height / 2 ? index : index + 1;

      if (slot !== target) {
        target = slot;
        setDragState({ from: rowIndex, target: slot });
      }
    };

    const handleUp = () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      setDragState(null);

      const toIndex = target > rowIndex ? target - 1 : target;

      if (toIndex === rowIndex || toIndex < 0) return;

      rowDraggingRef.current?.onReorder?.({
        component: instanceRef.current,
        fromIndex: rowIndex,
        toIndex,
        itemData: rows[rowIndex]?.data,
        itemElement: null,
        dropInsideItem: false,
      });
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
  }, []);

  const clickable = !!onRowClick || selectionMode !== "none";
  const rowCtx = useMemo(
    () => ({
      instance: instanceRef.current,
      locale,
      t,
      handlers: handlersRef,
      clickable,
      selectionMode,
      toggleRowSelection,
      startRowDrag,
      toggleGroup,
    }),
    [clickable, locale, selectionMode, startRowDrag, t, toggleGroup, toggleRowSelection]
  );

  const applyFilterRow = useCallback(
    (column, operation, value) => {
      const nextOperation = operation || DEFAULT_OPERATION[column.dataType] || "=";

      setFilterValue(
        setFilterRowCondition(stateRef.current.filterValue, column, nextOperation, value)
      );
    },
    [setFilterValue]
  );

  const applyHeaderFilter = useCallback(
    (column, type, values) =>
      setFilterValue(setHeaderFilterCondition(stateRef.current.filterValue, column, type, values)),
    [setFilterValue]
  );

  const loadHeaderFilterValues = useCallback(
    async column => {
      const translate = tRef.current;

      if (column.headerFilterDataSource) {
        const list =
          typeof column.headerFilterDataSource === "function"
            ? await column.headerFilterDataSource()
            : column.headerFilterDataSource;

        return (list || []).map(item => ({
          value: item.value,
          text: item.text ?? String(item.value),
        }));
      }

      if (column.dataType === "boolean")
        return [true, false].map(value => ({
          value,
          text: value ? column.trueText ?? translate("yes") : column.falseText ?? translate("no"),
        }));

      const { current } = sourceRef;
      const otherFilter = stateRef.current.filterEnabled
        ? buildDataSourceFilter(
            setHeaderFilterCondition(stateRef.current.filterValue, column, "anyof", []),
            columnsMap(columnsRef.current),
            "headerFilter"
          )
        : null;
      const selector = getColumnSelector(column);
      const date = isDateType(column.dataType);
      const group = date
        ? [
            { selector, groupInterval: "year", isExpanded: true },
            { selector, groupInterval: "month", isExpanded: true },
            { selector, groupInterval: "day", isExpanded: false },
          ]
        : [{ selector, desc: false, isExpanded: false }];
      let groupsResult;

      if (current.kind === "local" || !remoteRef.current) {
        const items =
          current.kind === "local" ? current.getItems() || [] : (await current.load({})).data;

        groupsResult = queryLocal(items, { filter: otherFilter, group }, getGetter).data;
      } else {
        const result = await current.load({
          filter: otherFilter,
          group,
          ...(date ? {} : { skip: 0, take: 500, requireGroupCount: true }),
        });

        groupsResult = result.data;
      }

      if (date) {
        const nodes = [];

        (groupsResult || []).forEach(year => {
          if (year.key == null) {
            nodes.push({ value: null, text: translate("gridBlank") });
            return;
          }

          nodes.push({
            value: String(year.key),
            text: String(year.key),
            children: (year.items || []).map(month => ({
              value: `${year.key}/${month.key}`,
              month: month.key,
              text: String(month.key),
              children: (month.items || []).map(day => ({
                value: `${year.key}/${month.key}/${day.key}`,
                text: String(day.key),
              })),
            })),
          });
        });

        return nodes;
      }

      return (groupsResult || []).map(item => ({
        value: item.key,
        text:
          item.key == null || item.key === ""
            ? translate("gridBlank")
            : getValueText(column, item.key, localeRef.current, "headerFilter"),
      }));
    },
    [getGetter]
  );

  const groupById = useCallback((columnId, index) => {
    const column = columnsRef.current.find(item => item.id === columnId);

    if (!column || !column.allowGrouping) return;

    dispatch({ type: "group", id: columnId, index });
  }, []);

  const dropColumn = useCallback((draggedId, targetId, before) => {
    const { current } = stateRef;

    if (!current.columns[targetId]) return;

    if (typeof current.columns[draggedId]?.groupIndex === "number")
      dispatch({ type: "ungroup", id: draggedId });

    const ordered = columnsRef.current
      .slice()
      .sort((a, b) => current.columns[a.id].order - current.columns[b.id].order)
      .filter(column => column.id !== draggedId);
    const targetPosition = ordered.findIndex(column => column.id === targetId);

    dispatch({
      type: "move",
      id: draggedId,
      targetOrder: before ? targetPosition : targetPosition + 1,
      columns: columnsRef.current,
    });
  }, []);

  const startResize = useCallback((event, column, headerCell) => {
    if (event.button !== 0 || !headerCell) return;

    event.preventDefault();
    event.stopPropagation();

    const table = tableRef.current;
    const viewColumn = viewColumnsRef.current.find(item => item.key === column.id);

    if (!table || !viewColumn) return;

    const col = table.querySelector(`colgroup > col[data-col-index="${viewColumn.index}"]`);
    const handle = event.currentTarget;
    const startX = event.clientX;
    const startWidth = headerCell.getBoundingClientRect().width;
    const startTableWidth = table.getBoundingClientRect().width;
    const minWidth = Math.max(column.minWidth ?? 0, 40);
    let widthNow = startWidth;

    handle.classList.add("cg-resizing");

    const handleMove = moveEvent => {
      widthNow = Math.max(startWidth + moveEvent.clientX - startX, minWidth);

      if (col) col.style.width = `${widthNow}px`;
      table.style.width = `${startTableWidth + widthNow - startWidth}px`;
    };

    const handleUp = () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      handle.classList.remove("cg-resizing");

      const { widths } = layoutRef.current;

      visibleColumnsRef.current.forEach(item => {
        if (
          item.id !== column.id &&
          !stateRef.current.columns[item.id]?.width &&
          !parseWidth(item.width)
        )
          dispatch({ type: "columnOption", id: item.id, patch: { width: widths[item.id] } });
      });

      dispatch({ type: "columnOption", id: column.id, patch: { width: Math.round(widthNow) } });
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
  }, []);

  const autoFitColumn = useCallback(column => {
    dispatch({ type: "columnOption", id: column.id, patch: { width: undefined } });
    setNatural(current => ({ ...current, key: null }));
  }, []);

  const toggleSort = useCallback(
    (column, event) => {
      const columnState = stateRef.current.columns[column.id];
      const multiple = sortingMode === "multiple" && (event.shiftKey || event.ctrlKey);

      if ((event.ctrlKey || event.metaKey) && !multiple) {
        dispatch({ type: "sort", id: column.id, order: null, multiple: false });
        return;
      }

      dispatch({
        type: "sort",
        id: column.id,
        order: columnState?.sortOrder === "asc" ? "desc" : "asc",
        multiple,
      });
    },
    [sortingMode]
  );

  const sortCount = Object.values(state.columns).filter(
    columnState => columnState.sortOrder && typeof columnState.groupIndex !== "number"
  ).length;

  const hasColumnMenu = column =>
    column.hasData &&
    ((sortingMode !== "none" && column.allowSorting) ||
      (groupPanelVisible && column.allowGrouping) ||
      columnFixingEnabled ||
      columnChooserEnabled);

  const dataRowKeys = data.rows.filter(row => row.rowType === "data").map(row => row.key);

  const headerCtx = {
    instance: instanceRef.current,
    t,
    sortingMode,
    multipleSort: sortingMode === "multiple",
    sortCount,
    headerFilterVisible,
    hasColumnMenu,
    allowColumnResizing,
    allowColumnReordering,
    allowSelectAll: selectionMode === "multiple" && selectionOptions.allowSelectAll === true,
    selectAllState: () => {
      const count = dataRowKeys.filter(key => selectedSet.has(key)).length;

      return { all: dataRowKeys.length > 0 && count === dataRowKeys.length, some: count > 0 };
    },
    selectAllLoaded: checked => {
      if (checked) selectRows(dataRowKeys, true);
      else deselectRows(dataRowKeys);
    },
    isColumnFiltered: column => !!getHeaderFilterCondition(state.filterValue, column),
    canDragColumn: column =>
      (allowColumnReordering && column.allowReordering) ||
      (groupPanelVisible && column.allowGrouping),
    setDraggedColumn: columnId => {
      draggedColumnRef.current = columnId;
    },
    getDraggedColumn: () => draggedColumnRef.current,
    dropColumn,
    toggleSort,
    openColumnMenu: (column, anchorEl, position) => setMenuTarget({ column, anchorEl, position }),
    openHeaderFilter: (column, anchorEl) => setHeaderFilterTarget({ column, anchorEl }),
    startResize,
    autoFitColumn,
  };

  const filterCtx = {
    t,
    locale,
    getFilterRowCondition: column => getFilterRowCondition(state.filterValue, column),
    getSelectedOperation: column =>
      selectedOperationsRef.current.get(column.id) ||
      column.selectedFilterOperation ||
      DEFAULT_OPERATION[column.dataType] ||
      "=",
    setSelectedOperation: (column, operation) => {
      if (operation) selectedOperationsRef.current.set(column.id, operation);
      else selectedOperationsRef.current.delete(column.id);
    },
    applyFilterRow,
  };

  const openColumnChooser = anchor =>
    setChooserAnchor(
      anchor ||
        rootRef.current?.querySelector(".cg-toolbar [data-grid-chooser]") ||
        rootRef.current?.querySelector(".cg-header-row") ||
        rootRef.current
    );

  const menuCtx = {
    t,
    sortingMode,
    groupingEnabled: groupPanelVisible,
    groupCount: groups.length,
    columnFixingEnabled,
    columnChooserEnabled,
    allowColumnResizing,
    getColumnState,
    setSort: (column, order) => dispatch({ type: "sort", id: column.id, order, multiple: false }),
    group: column => dispatch({ type: "group", id: column.id }),
    ungroup: column => dispatch({ type: "ungroup", id: column.id }),
    ungroupAll: () => dispatch({ type: "ungroupAll" }),
    fixColumn: (column, position) =>
      setColumnOption(
        column,
        position ? { fixed: true, fixedPosition: position } : { fixed: false }
      ),
    hideColumn: column => setColumnOption(column, { visible: false }),
    openColumnChooser: () => openColumnChooser(),
    autoFitColumn,
  };

  const headerFilterCtx = useMemo(
    () => ({
      t,
      language: i18n.language,
      headerFilterAllowSearch: childOptions.headerFilter?.allowSearch,
      loadHeaderFilterValues,
      getHeaderFilterCondition: column =>
        getHeaderFilterCondition(stateRef.current.filterValue, column),
      applyHeaderFilter,
    }),
    [
      applyHeaderFilter,
      childOptions.headerFilter?.allowSearch,
      i18n.language,
      loadHeaderFilterValues,
      t,
    ]
  );

  const filterableColumns = useMemo(
    () =>
      columns.filter(
        column =>
          column.hasData &&
          (column.allowFiltering || column.allowHeaderFiltering) &&
          getColumnSelector(column)
      ),
    [columns]
  );

  const panelCtx = {
    t,
    locale,
    filterValue: state.filterValue,
    filterEnabled: state.filterEnabled,
    columnsBySelector,
    filterableColumns,
    headerFilter: headerFilterCtx,
    setFilterValue,
    setFilterEnabled: enabled => dispatch({ type: "filterEnabled", enabled }),
    clearFilter,
    openFilterBuilder: () => setBuilderOpen(true),
  };

  const toolbarCtx = {
    t,
    groupPanelVisible,
    groupPanelEmptyText: childOptions.groupPanel?.emptyPanelText,
    groupedColumns,
    groupToggleVisible: groupToggle && groupPanelVisible && groups.length > 0,
    setAllExpanded: expanded => dataRef.current.setAllExpanded(expanded),
    getDraggedColumnConfig: () =>
      columnsRef.current.find(column => column.id === draggedColumnRef.current),
    setDraggedColumn: columnId => {
      draggedColumnRef.current = columnId;
    },
    groupById,
    setSort: (column, order) => dispatch({ type: "sort", id: column.id, order, multiple: false }),
    ungroup: column => dispatch({ type: "ungroup", id: column.id }),
    exportEnabled,
    exportFormats: exportOptions.formats || ["xlsx"],
    exportTo,
    columnChooserEnabled,
    openColumnChooser,
  };

  const items = [
    ...(toolbarItems || []),
    ...(childOptions.toolbar?.items || DEFAULT_TOOLBAR_ITEMS),
  ];

  const inherit = height == null || height === "inherit";
  const headerRowsHeight = ROW_HEIGHT * (filterRowVisible ? 2 : 1);
  const scrollMaxHeight = inherit
    ? headerRowsHeight + INHERIT_HEIGHT_MAX_ROWS * ROW_HEIGHT + 18
    : undefined;

  const rootClasses = ["consuman-grid"];

  if (showBorders) rootClasses.push("cg-bordered");
  if (showColumnLines) rootClasses.push("cg-column-lines");
  if (showRowLines) rootClasses.push("cg-row-lines");
  if (rowAlternationEnabled) rootClasses.push("cg-row-alternation");
  if (hoverStateEnabled) rootClasses.push("cg-hover");
  if (className) rootClasses.push(className);

  const groupContentOffset = level =>
    viewColumns
      .slice(0, viewColumns.findIndex(item => item.kind === "indent" && item.level === level) + 1)
      .reduce((sum, item) => sum + (item.width || 0), 0);

  const showSkeleton = data.phase === "initial" && data.rows.length === 0 && !data.error;
  const showEmpty =
    !showSkeleton && data.phase !== "initial" && data.rows.length === 0 && !data.error;
  const reloading =
    loadPanelEnabled &&
    (data.phase === "reloading" || (data.phase === "initial" && data.rows.length > 0));
  const showPager =
    mode === "standard" &&
    !data.grouped &&
    (pagerOptions.visible === true ||
      (pagerOptions.visible !== false &&
        (data.pageCount > 1 || !!pagerOptions.showPageSizeSelector)));

  let summary = "";

  if (data.totalCount >= 0)
    summary =
      data.groupCount >= 0 && groups.length
        ? t("gridRecordsInGroups", { count: data.totalCount, groups: data.groupCount })
        : t("gridRecords", { count: data.totalCount });

  let dropIndicatorTop = null;

  if (dragState && tableRef.current) {
    const lastIndex = Math.min(dragState.target, data.rows.length - 1);
    const rowElement = tableRef.current.querySelector(`tbody tr[data-row-index="${lastIndex}"]`);

    if (rowElement)
      dropIndicatorTop =
        dragState.target >= data.rows.length
          ? rowElement.offsetTop + rowElement.offsetHeight
          : rowElement.offsetTop;
  }

  let alternate = false;

  return (
    <GridRoot
      ref={rootRef}
      id={id}
      className={rootClasses.join(" ")}
      style={{ height: inherit ? undefined : height, width, ...style }}
    >
      <GridToolbar
        ctx={toolbarCtx}
        items={items}
      />
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          flex: "1 1 auto",
          minHeight: 0,
        }}
      >
        {reloading ? (
          <Box
            className="cg-loading"
            sx={{
              top: filterRowVisible
                ? `calc(var(--cg-header-height, ${ROW_HEIGHT}px) + ${ROW_HEIGHT}px)`
                : `var(--cg-header-height, ${ROW_HEIGHT}px)`,
            }}
          >
            <Box className="cg-loading-panel">
              <CircularProgress size={32} />
            </Box>
          </Box>
        ) : null}
        <Box
          ref={scrollRef}
          className="cg-scroll"
          onScroll={handleScroll}
          sx={{ maxHeight: scrollMaxHeight }}
          tabIndex={-1}
        >
          <table
            ref={tableRef}
            className={`cg-table${measuring ? " cg-measuring" : ""}`}
            style={measuring ? undefined : { width: layout.total }}
            role="grid"
            aria-busy={data.phase !== "idle"}
          >
            <colgroup>
              {viewColumns.map(viewColumn => {
                const autoWidth =
                  measuring &&
                  viewColumn.kind === "data" &&
                  !parseWidth(viewColumn.column.width) &&
                  !state.columns[viewColumn.key]?.width;

                return (
                  <col
                    key={viewColumn.key}
                    data-col-index={viewColumn.index}
                    style={autoWidth ? undefined : { width: viewColumn.width }}
                  />
                );
              })}
            </colgroup>
            <thead>
              <HeaderRow
                viewColumns={viewColumns}
                ctx={headerCtx}
              />
              {filterRowVisible ? (
                <FilterRow
                  viewColumns={viewColumns}
                  ctx={filterCtx}
                />
              ) : null}
            </thead>
            <tbody ref={bodyRef}>
              {data.rows.map((row, rowIndex) => {
                if (row.rowType === "group") {
                  alternate = false;

                  return (
                    <GroupRow
                      key={`g:${JSON.stringify(row.key)}`}
                      row={row}
                      rowIndex={rowIndex}
                      viewColumns={viewColumns}
                      ctx={rowCtx}
                      version={
                        renderVersion * 100000 +
                        (rowVersionsRef.current.get(JSON.stringify(row.key)) || 0)
                      }
                      groupColumn={groups[row.groupIndex]?.column}
                      contentOffset={groupContentOffset(row.groupIndex)}
                      viewportWidth={containerWidth}
                    />
                  );
                }

                const isAlternate = alternate;

                alternate = !alternate;

                return (
                  <DataRow
                    key={
                      row.key != null && typeof row.key !== "object"
                        ? `d:${row.key}`
                        : `i:${rowIndex}`
                    }
                    row={row}
                    rowIndex={rowIndex}
                    viewColumns={viewColumns}
                    ctx={rowCtx}
                    selected={selectedSet.has(row.key)}
                    alternate={isAlternate}
                    version={renderVersion * 100000 + (rowVersionsRef.current.get(row.key) || 0)}
                    dragging={dragState?.from === rowIndex}
                  />
                );
              })}
              {showSkeleton
                ? Array.from({ length: 5 }, (_, index) => (
                    <tr key={`skeleton${index}`}>
                      {viewColumns.map(viewColumn => (
                        <td key={viewColumn.key}>
                          {viewColumn.kind === "data" ? (
                            <Skeleton
                              variant="text"
                              sx={{ width: `${55 + ((index * 17 + viewColumn.index * 13) % 40)}%` }}
                            />
                          ) : null}
                        </td>
                      ))}
                    </tr>
                  ))
                : null}
              {data.phase === "more" ? (
                <tr>
                  <td colSpan={viewColumns.length}>
                    <Box
                      className="cg-empty"
                      sx={{ height: ROW_HEIGHT, width: containerWidth || "100%" }}
                    >
                      <CircularProgress size={16} />
                      <Typography variant="body2">{t("gridLoading")}</Typography>
                    </Box>
                  </td>
                </tr>
              ) : null}
              {data.error ? (
                <tr>
                  <td colSpan={viewColumns.length}>
                    <Box
                      className="cg-empty"
                      sx={{ width: containerWidth || "100%", color: "error.main" }}
                    >
                      <Iconify
                        icon="eva:alert-triangle-outline"
                        width={20}
                      />
                      <Typography variant="body2">{t("gridLoadError")}</Typography>
                      <Button
                        size="small"
                        variant="soft"
                        color="error"
                        onClick={() => data.retry()}
                      >
                        {t("gridRetry")}
                      </Button>
                    </Box>
                  </td>
                </tr>
              ) : null}
              {showEmpty ? (
                <tr>
                  <td colSpan={viewColumns.length}>
                    <Box
                      className="cg-empty"
                      sx={{ width: containerWidth || "100%" }}
                    >
                      <Iconify
                        icon="eva:inbox-outline"
                        width={22}
                      />
                      <Typography variant="body2">{noDataText || t("gridNoData")}</Typography>
                    </Box>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          {dropIndicatorTop != null ? (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                top: dropIndicatorTop - 1,
                height: 2,
                bgcolor: "primary.main",
                pointerEvents: "none",
                zIndex: 6,
              }}
            />
          ) : null}
        </Box>
      </Box>
      {filterPanelVisible ? (
        <FilterPanel
          ctx={panelCtx}
          summary={summary}
        />
      ) : null}
      {showPager ? (
        <Pager
          pageIndex={state.pageIndex}
          pageCount={data.pageCount}
          pageSize={state.pageSize}
          totalCount={data.totalCount}
          allowedPageSizes={allowedPageSizes}
          showPageSizeSelector={!!pagerOptions.showPageSizeSelector}
          onPageChange={pageIndex => dispatch({ type: "pageIndex", pageIndex })}
          onPageSizeChange={pageSize => dispatch({ type: "pageSize", pageSize })}
          t={t}
        />
      ) : null}
      <ColumnMenu
        target={menuTarget}
        ctx={menuCtx}
        onClose={() => setMenuTarget(null)}
      />
      <HeaderFilterPopover
        target={headerFilterTarget}
        ctx={headerFilterCtx}
        onClose={() => setHeaderFilterTarget(null)}
      />
      {chooserAnchor ? (
        <ColumnChooser
          anchorEl={chooserAnchor}
          columns={columns}
          ctx={{
            t,
            getColumnState,
            setColumnVisible: (column, visible) => setColumnOption(column, { visible }),
          }}
          onClose={() => setChooserAnchor(null)}
        />
      ) : null}
      <FilterBuilderDialog
        open={builderOpen}
        ctx={panelCtx}
        onClose={() => setBuilderOpen(false)}
      />
    </GridRoot>
  );
});

DataGrid.propTypes = {
  children: PropTypes.node,
  dataSource: PropTypes.any,
  keyExpr: PropTypes.any,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  id: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  showBorders: PropTypes.bool,
  showColumnLines: PropTypes.bool,
  showRowLines: PropTypes.bool,
  rowAlternationEnabled: PropTypes.bool,
  hoverStateEnabled: PropTypes.bool,
  allowColumnResizing: PropTypes.bool,
  allowColumnReordering: PropTypes.bool,
  noDataText: PropTypes.string,
  filterValue: PropTypes.any,
  defaultFilterValue: PropTypes.any,
  toolbarItems: PropTypes.array,
  groupToggle: PropTypes.bool,
  locale: PropTypes.object,
  onRowClick: PropTypes.func,
  onRowPrepared: PropTypes.func,
  onInitialized: PropTypes.func,
};

export default DataGrid;
